import { IRestaurantBankSettingsAcquirer, UserRoles, EntityStatus } from 'types';

export interface IRoles {
	id: string;
	name: UserRoles;
}

export interface IGetRoles {
	data: {
		roles: IRoles[];
	};
}

export interface ILanguages {
	id: TLanguageIDs;
	name: string;
}

export interface IGetLanguages {
	data: {
		languages: ILanguages[];
	};
}

export interface ICountriesAndCities {
	id: string;
	geoNameId: number;
	name: string;
}

export interface IGetCountries {
	data: {
		countries: ICountriesAndCities[];
	};
}

export interface IGetCities {
	data: {
		cities: ICountriesAndCities[];
	};
}

export interface IGetActualFilePath {
	data: { preSignedUrl: string };
}

export interface IGetActualFileObject {
	data: object;
}

export interface IPostMediaFile {
	data: { fileId: string; fileHash: Nullable<string> };
}

export interface IFileData {
	fileId: string;
	fileHash: Nullable<string>;
	fileName: Nullable<string>;
}

export interface ICurrencies {
	isoCurrencySymbol: string;
	currencyEnglishName: string;
}

export interface IGetCurrencies {
	data: { currencies: ICurrencies[] };
}

export interface IItemForGetShortInfo {
	id: string;
	name: string;
}

export interface IItemForGetShortInfoWithStatus extends IItemForGetShortInfo {
	status: EntityStatus;
}

export interface ITranslationInfo {
	languageId: TLanguageIDs;
	name: Nullable<string>;
	languageName: Nullable<string>;
	isDefault: boolean;
}

export interface ITranslationDetailedInfo extends ITranslationInfo {
	description?: Nullable<string>;
}

export interface ISelectOption {
	value: string;
	children: string;
}

export type TTextTranslationNamespaces =
	| 'noTranslation'
	| 'listing'
	| 'form'
	| 'localizationSettings'
	| 'mediaSettings'
	| 'error'
	| 'modals'
	| 'global'
	| 'routes'
	| 'notifications'
	| 'login';

export type TCountryCode =
	| 'GB'
	| 'RU'
	| 'UA'
	| 'DE'
	| 'CN'
	| 'FR'
	| 'IT'
	| 'PL'
	| 'ES'
	| 'SK'
	| 'DA'
	| 'BG'
	| 'RO'
	| 'HR'
	| 'CS'
	| 'NL'
	| 'ET'
	| 'FI'
	| 'EL'
	| 'HU'
	| 'GA'
	| 'LV'
	| 'LT'
	| 'MT'
	| 'PT'
	| 'SL'
	| 'SV';

export type TLanguageIDs =
	| '58d0e8c7-a20d-40e1-a4d6-fd701a913eff'
	| '2d95f7a5-7a8d-4e3d-bb59-769f3f95970d'
	| '475717ea-2f6f-4545-a0b0-98bbc483564e'
	| '973e4eb6-6e29-49ee-8455-d29613d30d2d'
	| '9bb106f2-ad52-4a57-a0c1-7b5ced1f55f5'
	| '75b14f56-cd51-4b1b-98c0-01effbd5cefc'
	| '7e9ada27-9886-4838-b8b0-6ba39f5faa5d'
	| '6f2e7f3f-7fa9-47d2-8289-9f4dd8b84060'
	| '503bc1c9-4fba-4525-bcd2-d78196eefc15'
	| '79d69d6c-9192-44f1-ad68-92939536a7b7'
	| '7463d99a-2f70-423f-972b-707338c8945a'
	| '7ae26617-a564-47ed-8722-5545151999d8'
	| 'd7f7b7c9-78f8-4ab5-bc9d-cc6eb7cf7831'
	| '7a6ae23b-56c0-4b14-b7fe-7be35707e658'
	| 'a07a15bb-9d7b-4be4-9f61-2a32dba9a24f'
	| 'a39f8dca-64a2-4902-88f7-7673addd79ac'
	| 'd42f5c1a-5509-458d-9126-3961e24b1797'
	| '15a9d0fb-25b7-4976-9430-a1d1ef501a29'
	| 'ec893fbc-e53a-498c-a67f-716149f66a28'
	| '0e3734e1-06db-423d-acd4-7b755f079d63'
	| 'a2b9b0df-7294-4200-b09d-c224999ab7d3'
	| '4ef4e46b-3bdc-44e0-ab74-ff6705818e3e'
	| 'c47fb2aa-a1ab-4ed6-8195-16b8117740bb'
	| 'b1b3097d-330a-48a1-8ad5-ab245dfb7df7'
	| '9574bb62-4914-4867-a4e7-6019232fd611'
	| '6c67e2f5-7183-4511-acd2-2952ce1c4026'
	| '9d749652-b046-4b54-97a9-98897c054f13';

export type TAvailableCountries = {
	[key in TLanguageIDs]: {
		countryCode: TCountryCode;
		languageName: string;
	};
};

export interface IDefaultLanguage {
	id: TLanguageIDs;
	name: string;
}

export interface IFileInBase64 {
	fullFileName: string;
	base64Content: string;
}

export type TLoaderType = 'big' | 'small' | 'avatar';

export type TFileId = Nullable<string>;

export interface IAppError extends Error {
	id: string;
	retryHandler?: () => void;
}

export enum ServerErrorTypes {
	MODIFIER_VALIDATION_FAILED = 'ModifierGroupValidationFailed',
	COMBO_GROUP_VALIDATION_FAILED = 'ComboGroupDishedValidationFailed',
	COMBO_ITEM_VALIDATION_FAILED = 'ComboItemValidationFailed',
	ADDITIONAL_DISH_VALIDATION_FAILED = 'AdditionalDishRestaurantsValidationFailed',
	DISH_MODIFICATION_VALIDATION_FAILED = 'DishModificationRestaurantsValidationFailed',
	COMBO_MODIFIER_VALIDATION_FAILED = 'ComboModifierValidationFailed',
	SUGGESTED_DISH_VALIDATION_FAILED = 'SuggestedDishRestaurantsValidationFailed',
	MODIFIER_GROUP_VALIDATION_FAILED = 'DishRestaurantsValidationFailed',
	SUB_CATEGORY_VALIDATION_FAILED = 'CategoryValidationFailed',
	FIRMWARE_DELETION_FAILED = 'FirmwareDeletionFailed',
	TABLET_APP_DELETION_FAILED = 'TabletAppDeletionFailed',
	RESTAURANT_DELETION_FAILED = 'DeleteRestaurantValidationFailed',
	DEVICE_HARDWARE_ID_VALIDATION_FAILED = 'HardwareIdExistsValidationFailed',
	SERIAL_NUMBER_VALIDATION_FAILED = 'SerialNumberExistsValidationFailed',
	USER_EMAIL_VALIDATION_FAILED = 'EmailExistsInvalidInput',
	GAME_APPLICATION_ID_VALIDATION_FAILED = 'GameApplicationIdExistsInvalidInput',
	CUSTOMER_NAME_ALREADY_EXIST = 'CustomerNameAlreadyExist',
	MENU_CIRCULAR_REFERENCE_VALIDATION_FAILED = 'MenuCircularReference',
	PRODUCT_HAS_PORTIONS = 'ProductHasPortions',
	BPA_VALIDATION_FAILED = 'BpaValidationFailed',
}

export type TDishValidationType =
	| ServerErrorTypes.SUGGESTED_DISH_VALIDATION_FAILED
	| ServerErrorTypes.ADDITIONAL_DISH_VALIDATION_FAILED
	| ServerErrorTypes.DISH_MODIFICATION_VALIDATION_FAILED;

export interface ICreateModifierValidationError {
	restaurants: { restaurantId: string; modifierGroups: string[] }[];
}

export interface ICreateSubCategoryValidationError {
	restaurantIds: string[];
}

export interface ICreateModifierGroupValidationError {
	restaurants: { restaurantId: string; dishes: string[] }[];
}

export interface IComboItemValidationError {
	comboItems: string[];
}

export interface ICreateDishValidationError {
	restaurants: { restaurantId: string; dishes: string[] }[];
}

export interface IDeleteApkFirmwareValidationError {
	restaurants: { restaurantId: string; name: string }[];
}

export interface IDeviceHardwareIdValidationError {
	hardwareId: string;
}

export interface ISerialNumberExistsValidationError {
	serialNumber: string;
}

export interface IUserEmailValidationError {
	email: string;
}

export interface IGameApplicationIdValidationError {
	applicationId: string;
}

export interface ICircularReferenceValidationError {
	modifierGroupId: string;
	modifierGroupName: string;
	modifierId: string;
	modifierName: string;
}

export interface IFirmwareApkRestaurants {
	id: string;
	name: string | null;
	uploadFileStatus: 'Pending' | 'Completed' | 'Failed';
}

export interface IServerError<T = null> {
	Message: string;
	Metadata: T;
	Type: ServerErrorTypes;
}

export enum RequestEntity {
	Users = 'Users',
	Customers = 'Customers',
	Categories = 'Categories',
	CategoriesMenuV2 = 'category-photos',
	SubCategories = 'SubCategories',
	SubcategoriesMenuV2 = 'subcategory-photos',
	Dishes = 'Dishes',
	DishesMenuV2 = 'dish-photos',
	Games = 'Games',
	Screensavers = 'Screensavers',
	Videos = 'Videos',
	Modifiers = 'Modifiers',
}

export interface IDescriptionOfAspectRatio {
	horizontal: {
		ratio: string;
		optimalResolutions: {
			a: string;
			b: string;
		};
	};
	vertical: {
		ratio: string;
		optimalResolutions: {
			a: string;
			b: string;
		};
	};
}

export type TPhotoSize = 'Original' | 'Medium' | 'Small';

export interface IDataGetMediaFilePathResponse {
	id: string;
	preSignedUrl: Nullable<string>;
}

export interface IGetMediaFilePathResponse {
	data: IDataGetMediaFilePathResponse;
}

export interface IGetMediaFilePathArgs {
	resource: RequestEntity;
	entityID: string;
	size: TPhotoSize;
}

export type TFileExtension =
	| 'jpg'
	| 'jpeg'
	| 'png'
	| 'gif'
	| 'mp4'
	| 'pdf'
	| 'zip'
	| 'bin'
	| 'apk'
	| 'html'
	| 'json'
	| 'p12';

export interface IMaxFileSizeMBStyleSettings {
	img: number;
	gif: number;
}

export interface IMenuTranslation {
	languageId: TLanguageIDs;
	name: Nullable<string>;
	description?: Nullable<string>;
}

export interface ICustomerLanguageInfo {
	id: string;
	name: Nullable<string>;
	defaultLanguageId: TLanguageIDs;
	languages: ILanguages[];
	status: EntityStatus;
}

export interface IGetBankSettingsAcquirers {
	ids: string[] | null;
}

export interface IGetAcquirers {
	data: {
		acquirers: IRestaurantBankSettingsAcquirer[];
	};
}

export type TCountOfItems = '12' | '24' | '48';

export type TKeyOfFeatureFlags =
	| 'SHOW_PAYMENT_ID_ON_RESTAURANT_FEATURE'
	| 'SHOW_EXTRA_PAYMENT_TYPES_ON_RESTAURANT_FEATURE'
	| 'HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND'
	| 'HAS_ADYEN_ACQUIRER'
	| 'HAS_PORTION_IN_MENU_V2'
	| 'HAS_NFC_CARDS'
	| 'HAS_CHARITY'
	| 'HAS_KM_VAT_ID';

export type TFeatureFlags = {
	[K in TKeyOfFeatureFlags]?: boolean;
};

export interface IGetFeatureFlags {
	data: TFeatureFlags;
}

export enum DirectionForPaginated {
	Ascending = 'Ascending',
	Descending = 'Descending',
}
